/**
 * @fileoverview Event management page component.
 * @summary Page for organizers to manage a specific event.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/EventManagementPage
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faEdit,
  faQrcode,
  faCheckCircle,
  faExclamationTriangle,
  faTimes,
  // faTrash,
  faEyeSlash,
  faPlay,
  faStop,
  faCircleXmark,
  faBolt,
} from '@fortawesome/free-solid-svg-icons'
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import { useAppDispatch, useAppSelector } from '../../hooks/redux.js'
import { fetchEvent, updateEvent, hideEvent, fetchEvents, type Event, clearError } from '../../store/slices/eventsSlice.js'
import { getEventSongLimit, shouldShowOneShotUpgrade, getBillingPeriod, getOrganizerLimits, type PlanLimitEvent } from '../../utils/planLimits.js'
import { getErrorMessage } from '../../utils/errorUtils.js'
import { utcToLocalDateString, utcToLocalTimeString, localToUtcISOString } from '../../utils/date.js'
import { SubscriptionType, SUBSCRIPTION_PLANS } from '@karaonow/shared'
import { POLLING_INTERVAL_MS } from '../../config/constants.js'
import { fetchVenues } from '../../store/slices/venuesSlice.js'
import {
  fetchQueue,
  updateQueuePosition,
  updateQueueStatus,
  removeSongFromQueue,
  type QueueItem,
  type QueueItemStatus,
} from '../../store/slices/queueSlice.js'
import { fetchSingers, removeSinger } from '../../store/slices/singersSlice.js'
import { createCheckoutSessionForEvent } from '../../services/api.js'
import Spinner from '../../components/Spinner.js'
import QRCodeDisplay from '../../components/QRCodeDisplay.js'
import PageHeader from '../../components/PageHeader.js'
import PlanLimitAlert from '../../components/PlanLimitAlert.js'
import { ConfirmModal } from '../../components/ConfirmModal.js'
import { EventEditForm } from './components/EventEditForm.js'
import { QueueSection } from './components/QueueSection.js'
import { SingersSection } from './components/SingersSection.js'
import { EventDetailsCard } from './components/EventDetailsCard.js'
import EventPlanBadge from './components/EventPlanBadge.js'
import UpgradeOneShotModal from './components/UpgradeOneShotModal.js'
import DateChangeQuotaModal from './components/DateChangeQuotaModal.js'
// import OneShotExpirationAlert from './components/OneShotExpirationAlert.js'


/**
 * @summary Get status badge class.
 */
function getStatusBadgeClass(status: Event['status']): string {
  switch (status) {
    case 'draft':
      return 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    case 'active':
      return 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200'
    case 'completed':
      return 'bg-blue-200 text-blue-800 dark:bg-blue-800 dark:text-blue-200'
    case 'cancelled':
      return 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200'
    case 'hidden':
      return 'bg-gray-400 text-gray-900 dark:bg-gray-600 dark:text-gray-100'
    default:
      return 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
  }
}

/**
 * @summary Event management page component.
 * @returns {React.ReactElement} Event management page.
 */
export default function EventManagementPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { eventId } = useParams<{ eventId: string }>()
  const { user } = useAppSelector((state) => state.auth)
  const { currentEvent, isLoading, error } = useAppSelector((state) => state.events)
  const { venues } = useAppSelector((state) => state.venues)
  const { items: queueItems, isLoading: isLoadingQueue, error: queueError } = useAppSelector((state) => state.queue)
  const { singers, isLoading: isLoadingSingers, error: singersError } = useAppSelector((state) => state.singers)

  // Get event song limit based on organizer plan + event plan
  // Cast Event to PlanLimitEvent (they are compatible, Event just has more fields)
  
  const songLimit = getEventSongLimit(user, currentEvent as PlanLimitEvent | null)

  const [showEditForm, setShowEditForm] = useState(false)
  const [showQRCode, setShowQRCode] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    venueId: '',
    date: '',
    time: '',
    status: 'draft' as Event['status'],
  })
  const [formError, setFormError] = useState<string | null>(null)
  const [formSuccess, setFormSuccess] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [queueActionError, setQueueActionError] = useState<string | null>(null)
  const [deleteQueueItemId, setDeleteQueueItemId] = useState<string | null>(null)
  const [deleteSingerId, setDeleteSingerId] = useState<string | null>(null)
  const [singersActionError, setSingersActionError] = useState<string | null>(null)
  const [deleteEventId, setDeleteEventId] = useState<string | null>(null)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [isUpgrading, setIsUpgrading] = useState(false)
  const [allEventsForQuota, setAllEventsForQuota] = useState<Event[]>([])
  const [showDateChangeQuotaModal, setShowDateChangeQuotaModal] = useState(false)
  const [quotaErrorData, setQuotaErrorData] = useState<{ message: string; currentCount?: number; limit?: number } | null>(null)
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)


  // Redirect if not authenticated or not organizer
  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true })
      return
    }
    if (user.role !== 'organizer' && user.role !== 'superadmin') {
      navigate('/', { replace: true })
      return
    }
  }, [user, navigate])

  // Fetch event and venues on mount
  useEffect(() => {
    if (eventId && user && (user.role === 'organizer' || user.role === 'superadmin')) {
      dispatch(fetchEvent(eventId))
      dispatch(fetchVenues())
      dispatch(fetchQueue(eventId))
      dispatch(fetchSingers(eventId))
      // Fetch all events (including hidden) for quota calculation
      dispatch(fetchEvents(true))
        .unwrap()
        .then((allEvents: Event[]) => {
          setAllEventsForQuota(allEvents)
        })
        .catch((err) => {
          console.error('Failed to fetch all events for quota:', err)
        })
    }
  }, [dispatch, eventId, user])

  // Update form data when event loads
  useEffect(() => {
    if (currentEvent) {
      // Convert UTC date from backend to local date/time for form inputs
      const eventDate = typeof currentEvent.date === 'string' ? new Date(currentEvent.date) : currentEvent.date
      const dateStr = utcToLocalDateString(eventDate)
      const timeStr = utcToLocalTimeString(eventDate)

      setFormData({
        name: currentEvent.name,
        description: currentEvent.description || '',
        venueId: currentEvent.venueId || '',
        date: dateStr,
        time: timeStr,
        status: currentEvent.status,
      })
    }
  }, [currentEvent])

  // Polling: Auto-refresh queue and singers every x minutes if event is active
  useEffect(() => {
    if (!eventId || !currentEvent || currentEvent.status !== 'active') {
      return
    }

    const intervalId = setInterval(() => {
      dispatch(fetchQueue(eventId))
      dispatch(fetchSingers(eventId))
    }, POLLING_INTERVAL_MS)

    // Cleanup interval on unmount or when dependencies change
    return () => {
      clearInterval(intervalId)
    }
  }, [eventId, currentEvent, dispatch])

  const handleEdit = () => {
    setShowEditForm(true)
    setFormError(null)
    setFormSuccess(null)
    setShowQRCode(false)
    dispatch(clearError())
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleCancelEdit = () => {
    setShowEditForm(false)
    // setFormError(null)
    // setFormSuccess(null)
    
    if (currentEvent) {
      // Convert UTC date from backend to local date/time for form inputs
      const eventDate = typeof currentEvent.date === 'string' ? new Date(currentEvent.date) : currentEvent.date
      const dateStr = utcToLocalDateString(eventDate)
      const timeStr = utcToLocalTimeString(eventDate)

      setFormData({
        name: currentEvent.name,
        description: currentEvent.description || '',
        venueId: currentEvent.venueId || '',
        date: dateStr,
        time: timeStr,
        status: currentEvent.status,
      })
    }
    setFormError(null)
    setFormSuccess(null)
    
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!eventId) return

    setIsSaving(true)
    setFormError(null)
    setFormSuccess(null)

    try {
      // Convert local date/time to UTC ISO string for backend
      const isoDateString = localToUtcISOString(formData.date, formData.time)

      const eventData = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        venueId: formData.venueId || undefined,
        date: isoDateString,
        status: formData.status,
      }

      await dispatch(updateEvent({ eventId, eventData })).unwrap()
      setFormSuccess(t('events.updateSuccess'))
      setShowEditForm(false)
      setTimeout(() => setFormSuccess(null), 3000)
      
    } catch (err: any) {
      // Extract error from payload (rejectWithValue) or use error directly
      const error = err.payload || err
      
      // Check if error is quota exceeded for date change (only for non-ONE_SHOT events)
      if (error.code === 'QUOTA_EXCEEDED_DATE_CHANGE' && currentEvent?.planType !== 'one_shot') {
        // Get plan name from user subscription
        const planType = (user?.subscription?.type as SubscriptionType) || SubscriptionType.FREE
        const planInfo = SUBSCRIPTION_PLANS[planType]
        const planName = planInfo?.displayName || 'Plan'
        
        // Get translated message with variables
        const translatedMessage = t('errors.QUOTA_EXCEEDED_DATE_CHANGE', {
          planName,
          limit: planInfo?.maxEvents || 0,
        })
        
        setQuotaErrorData({
          message: translatedMessage || error.message || t('events.quotaExceededDateChange') || 'Quota mensuel atteint pour cette date',
          currentCount: error.data?.currentCount,
          limit: error.data?.limit,
        })
        setShowDateChangeQuotaModal(true)
      } else {
        // Use error code for translation (handles EVENT_DATE_PAST, EVENT_DATE_AFTER_EXPIRATION, etc.)
        const errorMessage = getErrorMessage(error, t)
        setFormError(errorMessage || t('events.updateError'))
      }
    } finally {
      setIsSaving(false)
    }
  }

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Minimum drag distance before activation
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id || !eventId) return

    const oldIndex = queueItems.findIndex((item) => item.id === active.id)
    const newIndex = queueItems.findIndex((item) => item.id === over.id)

    if (oldIndex === -1 || newIndex === -1) return

    const item = queueItems[oldIndex]
    const newPosition = newIndex

    setQueueActionError(null)

    try {
      await dispatch(updateQueuePosition({ eventId, songId: item.id, position: newPosition })).unwrap()
      // Refresh queue to get updated positions
      await dispatch(fetchQueue(eventId))
    } catch (err: any) {
      setQueueActionError(err.message || t('events.queueUpdateError'))
    }
  }

  const handleStatusChange = async (item: QueueItem, status: QueueItemStatus) => {
    if (!eventId) return

    setQueueActionError(null)

    try {
      await dispatch(updateQueueStatus({ eventId, songId: item.id, status })).unwrap()
    } catch (err: any) {
     
      setQueueActionError(err.message || t('events.queueUpdateError'))
    }
  }

  const handleDeleteQueueItem = async () => {
    if (!eventId || !deleteQueueItemId) return

    setQueueActionError(null)

    try {
      await dispatch(removeSongFromQueue({ eventId, songId: deleteQueueItemId })).unwrap()
      setDeleteQueueItemId(null)
    } catch (err: any) {
      setQueueActionError(err.message || t('events.queueDeleteError'))
      setDeleteQueueItemId(null)
    }
  }

  const handleDeleteSinger = async () => {
    if (!eventId || !deleteSingerId) return

    setSingersActionError(null)

    try {
      await dispatch(removeSinger({ eventId, singerId: deleteSingerId })).unwrap()
      // Also refresh the queue since the singer's songs were removed
      await dispatch(fetchQueue(eventId))
      setDeleteSingerId(null)
    } catch (err: any) {
      setSingersActionError(err.message || t('events.singerDeleteError'))
      setDeleteSingerId(null)
    }
  }

  /**
   * @summary Change event status to active
   */
  const handleStartEvent = async () => {
    if (!eventId || !currentEvent) return

    setFormError(null)
    setFormSuccess(null)

    try {
      const eventData = {
        name: currentEvent.name,
        description: currentEvent.description,
        date: currentEvent.date,
        venueId: currentEvent.venueId,
        status: 'active' as const,
      }

      await dispatch(updateEvent({ eventId, eventData })).unwrap()
      setFormSuccess(t('events.statusUpdated'))
      setTimeout(() => setFormSuccess(null), 3000)
    } catch (err: any) {
      // Extract error from payload (rejectWithValue) or use error directly
      // console.log('err', err)
      const error = err.payload || err
      const errorMessage = getErrorMessage(error, t)
      // console.log('errorMessage', errorMessage)
      setFormError(errorMessage || t('events.updateError'))
    }
  }

  /**
   * @summary Change event status to completed
   */
  const handleCompleteEvent = async () => {
    if (!eventId || !currentEvent) return

    setFormError(null)
    setFormSuccess(null)

    try {
      const eventData = {
        name: currentEvent.name,
        description: currentEvent.description,
        date: currentEvent.date,
        venueId: currentEvent.venueId,
        status: 'completed' as const,
      }

      await dispatch(updateEvent({ eventId, eventData })).unwrap()
      setFormSuccess(t('events.statusUpdated'))
      setTimeout(() => setFormSuccess(null), 3000)
    } catch (err: any) {
      // Extract error from payload (rejectWithValue) or use error directly
      const error = err.payload || err
      const errorMessage = getErrorMessage(error, t)
      setFormError(errorMessage || t('events.updateError'))
    }
  }

  /**
   * @summary Open cancel confirmation modal
   */
  const handleCancelEvent = () => {
    setShowCancelConfirmModal(true)
  }

  /**
   * @summary Confirm event cancellation after user confirmation
   */
  const handleConfirmCancel = async () => {
    if (!eventId || !currentEvent) return

    setIsCancelling(true)
    setFormError(null)
    setFormSuccess(null)

    try {
      const eventData = {
        name: currentEvent.name,
        description: currentEvent.description,
        date: currentEvent.date,
        venueId: currentEvent.venueId,
        status: 'cancelled' as const,
      }

      await dispatch(updateEvent({ eventId, eventData })).unwrap()
      setFormSuccess(t('events.statusUpdated'))
      setTimeout(() => setFormSuccess(null), 3000)
      setShowCancelConfirmModal(false)
    } catch (err: any) {
      // Extract error from payload (rejectWithValue) or use error directly
      const error = err.payload || err
      const errorMessage = getErrorMessage(error, t)
      setFormError(errorMessage || t('events.updateError'))
    } finally {
      setIsCancelling(false)
    }
  }

  /**
   * @summary Upgrade event to ONE_SHOT plan
   */
  const handleUpgradeToOneShot = async () => {
    if (!eventId) return

    setIsUpgrading(true)
    setFormError(null)

    try {
      const response = await createCheckoutSessionForEvent(eventId)

      if (!response.success || !response.data?.url) {
        throw new Error(response.message || t('subscription.upgradeError') || 'Failed to create checkout session')
      }

      // Redirect to Stripe checkout
      window.location.href = response.data.url
    } catch (err: any) {
      setFormError(err.message || t('subscription.upgradeError') || 'Failed to upgrade event')
      setIsUpgrading(false)
      setShowUpgradeModal(false)
    }
  }

  /**
   * @summary Hide event (masquer)
   * @description Hides the current event. Only completed or cancelled events can be hidden.
   */
  const handleHideEvent = async () => {
    if (!eventId) return

    setFormError(null)
    setFormSuccess(null)

    try {
      await dispatch(hideEvent(eventId)).unwrap()
      setFormSuccess(t('events.hideSuccess') || 'Événement masqué avec succès')
      // Redirect to events list after hiding
      setTimeout(() => {
        navigate('/dashboard')
      }, 1500)
    } catch (err: any) {
      setFormError(err.message || (t('events.hideError') || 'Erreur lors du masquage de l\'événement'))
      setDeleteEventId(null)
    }
  }


  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-8 px-4">
        <Spinner size="lg" />
      </div>
    )
  }

  // Event not found or error
  if (!eventId || !currentEvent) {
    return (
      <div className="min-h-screen flex items-center justify-center py-8 px-4">
        <div className="text-center">
          <p className="text-(--color-text-secondary) text-lg mb-4">
            {error || t('errors.notFound')}
          </p>
        </div>
      </div>
    )
  }

  // qrCode already contains the path (e.g., "/event/abc123"), so just append to origin
  const eventUrl = `${window.location.origin}${currentEvent.qrCode}`
  const selectedVenue = venues.find((v) => v.id === currentEvent.venueId)
// console.log('error', error)
// console.log('formError', formError)
  return (
    <div className="min-h-screen">
      <div className="w-full page-container">
        <div className="grid grid-cols-6  gap-2 mb-0">
          <div className="col-span-1"></div>
          <div className="col-span-4">
            <PageHeader
            title={currentEvent.name}
            subtitle={t('events.managementTitle')}
          />
          </div>
          {/* Plan Badge */}
          <div className="col-span-1 mx-auto mt-2">
              <EventPlanBadge planType={currentEvent.planType} />
            </div>
        </div>

        {/* ONE_SHOT Expiration Alert */}
        {/* {currentEvent.planType === 'one_shot' && currentEvent.oneShotExpiresAt && (
          <OneShotExpirationAlert
            expiresAt={currentEvent.oneShotExpiresAt}
            className="mb-6"
          />
        )} */}

        {/* ONE_SHOT Validity Info */}
        {currentEvent.planType === 'one_shot' && currentEvent.oneShotPurchasedAt && (
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg mb-6">
            <p className="text-sm text-blue-900 dark:text-blue-200">
              ℹ️ {t('subscription.oneShotValidityInfo', { days: 15 }) || 'Cet achat est valable 15 jours à partir de la date d\'achat. Utilisez-le avant expiration !'}
            </p>
          </div>
        )}

        {/* Plan Badge and Upgrade Button */}
        {!showEditForm && (() => {
          // Calculate current events count in billing period for quota check
          const organizerLimits = getOrganizerLimits(user)
          const billingPeriod = getBillingPeriod(user)
          
          let currentEventsCountInPeriod: number | undefined
          if (organizerLimits.isMonthlyQuota && billingPeriod) {
            // Count events with date in billing period (exclude ONE_SHOT, include hidden)
            // Events created before subscription are counted if their date is in the billing period
            currentEventsCountInPeriod = allEventsForQuota.filter(e => {
              const eventDate = new Date(e.date) // Use event date, not creation date
              return (
                eventDate >= billingPeriod.start &&
                eventDate <= billingPeriod.end &&
                (e.planType === SubscriptionType.FREE || !e.planType)
              )
            }).length
          }

          return (
            <div className="flex flex-wrap items-center gap-3 mb-0">
              {/* Upgrade to ONE_SHOT Button - Centralized logic with quota check */}
              {shouldShowOneShotUpgrade(user, currentEvent as PlanLimitEvent, currentEventsCountInPeriod) && (
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="btn btn-sm w-full! bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-2 mb-4"
                >
                  <FontAwesomeIcon icon={faBolt} className="w-4 h-4" />
                  {t('subscription.upgradeToUnlimited') || '🚀 Passer en illimité'}
                </button>
              )}
            </div>
          )
        })()}

        {/* Plan Limit Alert for Songs - Centralized logic */}
        {songLimit.hasLimit && (
          <PlanLimitAlert
            limitReached={queueItems.length >= songLimit.maxSongs}
            currentCount={queueItems.length}
            limit={songLimit.maxSongs}
            limitType="songs"
            onUpgrade={() => setShowUpgradeModal(true)}
            className="mb-6"
          />
        )}
            
          {!showEditForm && (
              <>
                {/* Event Action Buttons - Status Management */}
                <div className="flex w-full gap-2 mb-4">
                  {/* Start - draft only */}
                  {currentEvent.status === 'draft' && (
                    <button
                      onClick={handleStartEvent}
                      className="event-action-start btn px-4!"
                      title={t('events.startEvent')}
                    >
                      <FontAwesomeIcon icon={faPlay} className="w-4 h-4" />
                      {t('events.start')}
                    </button>
                  )}

                  {/* Complete - active only */}
                  {currentEvent.status === 'active' && (
                    <button
                      onClick={handleCompleteEvent}
                      className="event-action-complete btn px-4!"
                      title={t('events.completeEvent')}
                    >
                      <FontAwesomeIcon icon={faStop} className="w-4 h-4" />
                      {t('events.complete')}
                    </button>
                  )}

                  {/* Cancel - draft or active */}
                  {(currentEvent.status === 'draft' || currentEvent.status === 'active') && (
                    <button
                      onClick={handleCancelEvent}
                      className="event-action-cancel btn px-4!"
                      title={t('events.cancelEvent')}
                    >
                      <FontAwesomeIcon icon={faCircleXmark} className="w-4 h-4" />
                      {t('events.cancel')}
                    </button>
                  )}

                  {/* Hide - only for completed or cancelled events */}
                  {(currentEvent.status === 'completed' || currentEvent.status === 'cancelled') && (
                    <button
                      onClick={() => setDeleteEventId(eventId)}
                      className="btn btn-secondary px-4!"
                      title={t('events.hide') || 'Masquer l\'événement'}
                    >
                      <FontAwesomeIcon icon={faEyeSlash} className="w-4 h-4" />
                      {t('events.hide') || 'Masquer'}
                    </button>
                  )}
                </div>

                {/* QR Code and Edit Buttons */}
                <div className="flex w-full gap-2 mb-4">
                  
                  <button onClick={handleEdit} className="btn btn-primary flex-1 px-4!">
                    <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                    {t('events.edit')}
                  </button>
                  <button onClick={() => setShowQRCode(!showQRCode)} className="btn btn-secondary flex-1 px-4!">
                    <FontAwesomeIcon icon={faQrcode} className="w-4 h-4" />
                    {showQRCode ? t('events.hideQRCode') : t('events.showQRCode')}
                  </button>
                </div>
              </>
            )}
        

        {/* Success/Error messages */}
        {formSuccess && (
          <div className="alert alert-success mb-6 flex items-center gap-2">
            <FontAwesomeIcon icon={faCheckCircle} />
            <span>{formSuccess}</span>
          </div>
        )}
        {formError && !showEditForm && (
          <div className="alert alert-error mb-6 flex items-center gap-2">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>{formError}</span>
          </div>
        )}

        {/* QR Code Display */}
        {showQRCode && !showEditForm && (
          <div className="mb-6 qr-code-container">
            <div className="flex items-center justify-between bg-(--color-bg-primary) pl-2 rounded-t-lg! mb-0">
              <h2 className="text-xl font-semibold text-(--color-text-primary)">
                {t('events.qrCodeTitle')}
              </h2>
              <button
                onClick={() => setShowQRCode(false)}
                className="text-(--color-text-secondary) hover:text-(--color-text-primary) pr-2 transition-colors"
                aria-label={t('common.close')}
              >
                <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
              </button>
            </div>
            <QRCodeDisplay qrCode={currentEvent.qrCode} eventName={currentEvent.name} eventUrl={eventUrl} />
          </div>
        )}

        {/* Edit Form */}
        <EventEditForm
          isOpen={showEditForm}
          formData={formData}
          isSaving={isSaving}
          formError={formError}
          venues={venues}
          onFormDataChange={(data) => setFormData({ ...formData, ...data })}
          onSubmit={handleSubmit}
          onCancel={handleCancelEdit}
          eventId={eventId || undefined}
          onDateQuotaError={(error) => {
            setQuotaErrorData({
              message: error.message,
              currentCount: error.currentCount,
              limit: error.limit,
            })
            setShowDateChangeQuotaModal(true)
          }}
          originalDate={currentEvent?.date ? (typeof currentEvent.date === 'string' ? currentEvent.date : currentEvent.date.toISOString()) : undefined}
          planType={currentEvent?.planType}
          oneShotExpiresAt={currentEvent?.oneShotExpiresAt}
        />

        {/* Event Details */}
        <EventDetailsCard
          isOpen={!showEditForm}
          event={currentEvent}
          selectedVenue={selectedVenue}
          getStatusBadgeClass={getStatusBadgeClass}
        />

        {/* Queue Section */}
        <QueueSection
          isOpen={!showEditForm}
          isLoading={isLoadingQueue}
          error={queueError}
          actionError={queueActionError}
          items={queueItems}
          sensors={sensors}
          onRefresh={() => eventId && dispatch(fetchQueue(eventId))}
          onDragEnd={handleDragEnd}
          onStatusChange={handleStatusChange}
          onDelete={(itemId) => setDeleteQueueItemId(itemId)}
          onActionErrorDismiss={() => setQueueActionError(null)}
        />

        {/* Singers Section */}
        <SingersSection
          isOpen={!showEditForm}
          isLoading={isLoadingSingers}
          error={singersError}
          actionError={singersActionError}
          singers={singers}
          onRefresh={() => eventId && dispatch(fetchSingers(eventId))}
          onDelete={(singerId) => setDeleteSingerId(singerId)}
        />

        {/* Delete Queue Item Confirmation Modal */}
        {deleteQueueItemId && (
          <ConfirmModal
            isOpen={!!deleteQueueItemId}
            onCancel={() => setDeleteQueueItemId(null)}
            onConfirm={handleDeleteQueueItem}
            title={t('events.deleteQueueItem')}
            message={t('events.deleteQueueItemConfirm')}
            confirmLabel={t('common.delete')}
            cancelLabel={t('common.cancel')}
            variant="danger"
          />
        )}

        {/* Delete Singer Confirmation Modal */}
        {deleteSingerId && (
          <ConfirmModal
            isOpen={!!deleteSingerId}
            onCancel={() => setDeleteSingerId(null)}
            onConfirm={handleDeleteSinger}
            title={t('events.deleteSinger')}
            message={t('events.deleteSingerConfirm')}
            confirmLabel={t('common.delete')}
            cancelLabel={t('common.cancel')}
            variant="danger"
          />
        )}

        {/* Hide Event Confirmation Modal */}
        {deleteEventId && (
          <ConfirmModal
            isOpen={!!deleteEventId}
            onCancel={() => setDeleteEventId(null)}
            onConfirm={handleHideEvent}
            title={t('events.hideConfirmTitle') || 'Masquer l\'événement'}
            message={t('events.hideConfirm')?.replace('{{name}}', currentEvent.name) || `Masquer l'événement "${currentEvent.name}" ? Il ne sera plus visible dans votre liste principale, mais restera comptabilisé dans votre quota mensuel.`}
            confirmLabel={t('events.hide') || 'Masquer'}
            cancelLabel={t('common.cancel') || 'Annuler'}
            variant="warning"
          />
        )}

        {/* Upgrade to ONE_SHOT Modal */}
        <UpgradeOneShotModal
          eventName={currentEvent.name}
          isOpen={showUpgradeModal}
          isLoading={isUpgrading}
          onConfirm={handleUpgradeToOneShot}
          onCancel={() => setShowUpgradeModal(false)}
        />

        {/* Date Change Quota Modal */}
        {currentEvent?.planType !== 'one_shot' && quotaErrorData && (
          <DateChangeQuotaModal
            eventName={currentEvent.name}
            isOpen={showDateChangeQuotaModal}
            isLoading={isUpgrading}
            quotaMessage={quotaErrorData.message}
            currentCount={quotaErrorData.currentCount}
            limit={quotaErrorData.limit}
            onUpgradeToOneShot={() => {
              setShowDateChangeQuotaModal(false)
              setShowUpgradeModal(true)
            }}
            onCancel={() => {
              setShowDateChangeQuotaModal(false)
              setQuotaErrorData(null)
              // Reset form to original date
              if (currentEvent) {
                // Convert UTC date from backend to local date/time for form inputs
                const eventDate = typeof currentEvent.date === 'string' ? new Date(currentEvent.date) : currentEvent.date
                const dateStr = utcToLocalDateString(eventDate)
                const timeStr = utcToLocalTimeString(eventDate)
                setFormData(prev => ({
                  ...prev,
                  date: dateStr,
                  time: timeStr,
                }))
              }
            }}
          />
        )}

        {/* Cancel Event Confirmation Modal */}
        <ConfirmModal
          isOpen={showCancelConfirmModal}
          title={currentEvent?.planType === 'one_shot'
            ? t('events.cancelConfirmOneShotTitle')
            : t('events.cancelConfirmTitle')}
          message={currentEvent?.planType === 'one_shot'
            ? t('events.cancelConfirmOneShotMessage')
            : t('events.cancelConfirmMessage')}
          variant="danger"
          confirmLabel={t('events.cancelConfirmButton')}
          cancelLabel={t('events.cancelCancelButton')}
          onConfirm={handleConfirmCancel}
          onCancel={() => setShowCancelConfirmModal(false)}
          isLoading={isCancelling}
        />
      </div>
    </div>
  )
}
